import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  GraduationCap, BookOpen, Calendar, Bell, Settings,
  User, LogOut, FileText, Globe, Award, Clock,
  ChevronRight, Mail, Building2, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import Layout from '@/components/Layout';

/* @section: quick-apps */
const APPS = [
  { label: '教务系统', icon: BookOpen, color: '#003087', desc: '课程表 · 成绩查询' },
  { label: '图书馆', icon: Building2, color: '#2d5a27', desc: '资源检索 · 借阅' },
  { label: '校园邮件', icon: Mail, color: '#1a3a6b', desc: '收发邮件 · 通知' },
  { label: '网络服务', icon: Globe, color: '#4a2d00', desc: 'VPN · 校园网' },
  { label: '科研平台', icon: Award, color: '#3a1a4a', desc: '成果 · 项目' },
  { label: '档案管理', icon: FileText, color: '#1a4a3a', desc: '证书 · 档案' },
];

/* @section: announcements */
const ANNOUNCEMENTS = [
  { title: '2026年秋季学期选课通知', time: '2026-06-05', tag: '教务', unread: true },
  { title: '图书馆数据库资源更新公告', time: '2026-06-03', tag: '图书馆', unread: true },
  { title: '研究生奖学金评定结果公示', time: '2026-06-01', tag: '研究生院', unread: false },
  { title: '2026年暑期国际交流项目招募', time: '2026-05-28', tag: '国际合作', unread: false },
];

/* @section: academic-calendar */
const CALENDAR_EVENTS = [
  { date: '06/10', event: '期末考试开始', type: 'exam' },
  { date: '06/20', event: '期末考试结束', type: 'exam' },
  { date: '07/01', event: '暑期假期开始', type: 'holiday' },
  { date: '08/31', event: '新学期注册报到', type: 'school' },
];

const TAG_COLORS: Record<string, string> = {
  教务: 'bg-blue-100 text-blue-700',
  图书馆: 'bg-green-100 text-green-700',
  研究生院: 'bg-purple-100 text-purple-700',
  国际合作: 'bg-amber-100 text-amber-700',
};

const EVENT_COLORS: Record<string, string> = {
  exam: 'text-red-600 bg-red-50 border-red-200',
  holiday: 'text-green-600 bg-green-50 border-green-200',
  school: 'text-blue-600 bg-blue-50 border-blue-200',
};

const ROLE_LABELS: Record<string, string> = {
  student: '学生',
  staff: '教职工',
  alumni: '校友',
  visitor: '访客',
};

export default function Dashboard() {
  const { user, profile, signOut, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth', { replace: true });
    }
  }, [user, loading, navigate]);

  const handleSignOut = async () => {
    await signOut();
    toast.success('已退出登录');
    navigate('/');
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center animate-pulse">
            <GraduationCap className="w-7 h-7 text-white" />
          </div>
          <p className="text-sm text-muted-foreground">加载中...</p>
        </div>
      </div>
    );
  }

  const initials = profile?.full_name
    ? profile.full_name.slice(0, 2).toUpperCase()
    : user.email?.slice(0, 2).toUpperCase() ?? 'U';
  const displayName = profile?.full_name ?? user.email?.split('@')[0] ?? '用户';
  const role = profile?.user_role ?? 'visitor';

  return (
    <Layout>
      {/* @section: dashboard-hero */}
      <section className="border-b border-border" style={{ background: 'linear-gradient(135deg, oklch(0.20 0.10 240) 0%, oklch(0.32 0.14 240) 100%)' }}>
        <div className="max-w-7xl mx-auto px-6 py-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <Avatar className="w-16 h-16 border-2 border-white/30">
              <AvatarImage src={profile?.avatar_url ?? undefined} />
              <AvatarFallback className="text-xl text-white font-bold" style={{ background: 'oklch(0.45 0.15 240)' }}>{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                  欢迎，{displayName}
                </h1>
                <Badge className="bg-white/20 text-white border-0 text-xs">{ROLE_LABELS[role]}</Badge>
              </div>
              <p className="text-white/60 text-sm mt-1">{user.email}</p>
              <p className="text-white/50 text-xs mt-1">登录时间：{new Date().toLocaleString('zh-CN')}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10 bg-transparent gap-2">
                <Settings className="w-4 h-4" />账号设置
              </Button>
              <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-red-500/20 bg-transparent gap-2" onClick={handleSignOut}>
                <LogOut className="w-4 h-4" />退出
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* @section: dashboard-body */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-6">

          {/* Left: Main content */}
          <div className="lg:col-span-2 space-y-6">

            {/* Quick Apps */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-primary" />常用功能
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {APPS.map((app) => (
                      <a key={app.label} href="#"
                        className="flex items-center gap-3 p-3 rounded-xl border border-border hover:border-primary/20 hover:shadow-md transition-all group cursor-pointer bg-background">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform"
                          style={{ background: app.color + '18' }}>
                          <app.icon className="w-4 h-4" style={{ color: app.color }} />
                        </div>
                        <div>
                          <div className="text-sm font-medium group-hover:text-primary transition-colors">{app.label}</div>
                          <div className="text-[10px] text-muted-foreground">{app.desc}</div>
                        </div>
                      </a>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Announcements */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <Card>
                <CardHeader className="pb-3 flex flex-row items-center justify-between">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Bell className="w-4 h-4 text-primary" />消息通知
                    <Badge className="bg-primary text-white text-xs border-0 ml-1">
                      {ANNOUNCEMENTS.filter(a => a.unread).length}
                    </Badge>
                  </CardTitle>
                  <a href="#" className="text-xs text-primary hover:underline flex items-center gap-1">
                    全部 <ChevronRight className="w-3 h-3" />
                  </a>
                </CardHeader>
                <CardContent className="space-y-1 pt-0">
                  {ANNOUNCEMENTS.map((a, i) => (
                    <div key={i}>
                      <a href="#" className="flex items-start gap-3 py-3 group">
                        <div className={`shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full ${a.unread ? 'bg-primary' : 'bg-transparent'}`} />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm leading-relaxed group-hover:text-primary transition-colors line-clamp-2 ${a.unread ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                            {a.title}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={`text-[10px] border-0 font-medium ${TAG_COLORS[a.tag]}`}>{a.tag}</Badge>
                            <span className="text-[10px] text-muted-foreground">{a.time}</span>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </a>
                      {i < ANNOUNCEMENTS.length - 1 && <Separator />}
                    </div>
                  ))}
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Right sidebar */}
          <div className="space-y-6">

            {/* Profile Card */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 }}>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <User className="w-4 h-4 text-primary" />个人信息
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    { label: '姓名', value: displayName, icon: User },
                    { label: '邮箱', value: user.email ?? '-', icon: Mail },
                    { label: '身份', value: ROLE_LABELS[role], icon: Shield },
                    { label: '部门', value: profile?.department ?? '未设置', icon: Building2 },
                  ].map(({ label, value, icon: Icon }) => (
                    <div key={label} className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <Icon className="w-3.5 h-3.5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] text-muted-foreground">{label}</div>
                        <div className="text-sm font-medium truncate">{value}</div>
                      </div>
                    </div>
                  ))}
                  <Separator />
                  <Button variant="outline" size="sm" className="w-full gap-2 text-xs">
                    <Settings className="w-3.5 h-3.5" />编辑个人信息
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Academic Calendar */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 }}>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-primary" />学历日历
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2.5 pt-0">
                  {CALENDAR_EVENTS.map((ev, i) => (
                    <div key={i} className={`flex items-center gap-3 p-2.5 rounded-lg border text-xs ${EVENT_COLORS[ev.type]}`}>
                      <div className="font-bold text-sm w-10 shrink-0 text-center">{ev.date}</div>
                      <div className="flex-1">{ev.event}</div>
                      <Clock className="w-3 h-3 shrink-0 opacity-60" />
                    </div>
                  ))}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
